import * as React from 'react';

import type * as api from '@gitbook/api-client';
import {
    Dialog,
    DialogBody,
    DialogHeader,
    LoadingPanel,
    Stepper,
    StepperStep,
    toast,
} from '@gitbook/spine-react';

import { IntegrationDialogAside } from '../../Integrations';
import featureFlagsIntegrationAssetDark from './assets/feature-flag-visual-dark.svg';
import featureFlagsIntegrationAssetShiftedDark from './assets/feature-flag-visual-dark-shifted.svg';
import featureFlagsIntegrationAssetLight from './assets/feature-flag-visual-light.svg';
import featureFlagsIntegrationAssetShiftedLight from './assets/feature-flag-visual-light-shifted.svg';
import { ConfigureIntegrationStep, InstallCompletedStep, InstallIntegrationStep } from './Steps';

/**
 * Dialog to setup the Adaptive content site with a feature flags management provider.
 */
export function FeatureFlagsIntegrationDialog(props: {
    organizationId: api.Organization['id'];
    siteId: api.Site['id'];
}): React.ReactElement | null {
    return (
        <Dialog size={{ width: 896, height: 640 }}>
            <FeatureFlagsIntegrationDialogContent {...props} />
        </Dialog>
    );
}

type SetupFeatureFlagsSteps =
    | {
          step: 'install_feature_flags_integration';
      }
    | {
          step: 'configure_integration';
          integration: api.Integration;
      }
    | {
          step: 'install_completed';
          integration: api.Integration;
      };

const setupFeatureFlagsSteps: { id: SetupFeatureFlagsSteps['step']; title: string }[] = [
    { id: 'install_feature_flags_integration', title: 'Pick a provider' },
    { id: 'configure_integration', title: 'Configure the integration' },
    { id: 'install_completed', title: 'Done' },
];

function FeatureFlagsIntegrationDialogContent(props: {
    organizationId: api.Organization['id'];
    siteId: api.Site['id'];
}) {
    const [currentStep, setCurrentStep] = React.useState<SetupFeatureFlagsSteps>({
        step: 'install_feature_flags_integration',
    });

    return (
        <div className="flex h-full max-md:flex-col">
            <IntegrationDialogAside
                integration={'integration' in currentStep ? currentStep.integration : undefined}
                backgroundSrc={{
                    light:
                        currentStep.step === 'install_feature_flags_integration'
                            ? featureFlagsIntegrationAssetLight
                            : featureFlagsIntegrationAssetShiftedLight,
                    dark:
                        currentStep.step === 'install_feature_flags_integration'
                            ? featureFlagsIntegrationAssetDark
                            : featureFlagsIntegrationAssetShiftedDark,
                }}
            />
            <div className="flex h-full flex-1 flex-col">
                <DialogHeader>
                    <Stepper<SetupFeatureFlagsSteps['step']>
                        value={currentStep.step}
                        onChange={() => {}}
                    >
                        {setupFeatureFlagsSteps.map((step) => (
                            <StepperStep key={step.id} id={step.id} title={step.title} />
                        ))}
                    </Stepper>
                </DialogHeader>

                <DialogBody className="flex-1">
                    <div className="h-full">
                        <React.Suspense fallback={<LoadingPanel size="large" />}>
                            {currentStep.step === 'install_feature_flags_integration' ? (
                                <InstallIntegrationStep
                                    {...props}
                                    onNext={(integration) => {
                                        if (integration.configurations?.site) {
                                            setCurrentStep({
                                                step: 'configure_integration',
                                                integration,
                                            });
                                        } else {
                                            toast.error(
                                                'Failed to install the feature flags integration on this site',
                                                null
                                            );
                                        }
                                    }}
                                />
                            ) : null}
                            {currentStep.step === 'configure_integration' ? (
                                <ConfigureIntegrationStep
                                    {...props}
                                    integration={currentStep.integration}
                                    onNext={() => {
                                        setCurrentStep({
                                            step: 'install_completed',
                                            integration: currentStep.integration,
                                        });
                                    }}
                                />
                            ) : null}
                            {currentStep.step === 'install_completed' ? (
                                <InstallCompletedStep
                                    {...props}
                                    integration={currentStep.integration}
                                />
                            ) : null}
                        </React.Suspense>
                    </div>
                </DialogBody>
            </div>
        </div>
    );
}
